import { Droplets } from "lucide-react";

/**
 * Animated watery badge used in the hero.
 * - Flowing liquid SVG waves behind the text
 * - Text filled with a moving cyan→green gradient ("water ink")
 * - Subtle bubbles rising
 */
export default function WaterBadge({ className = "" }) {
  return (
    <div
      data-testid="water-badge"
      className={`relative overflow-hidden rounded-2xl border border-[#29ABE2]/25 dark:border-[#29ABE2]/35 bg-gradient-to-br from-[#E6F7FD] via-[#F2FAF4] to-[#E3F5EA] dark:from-[#0B2C3A] dark:via-[#0E2A17] dark:to-[#12331E] px-5 md:px-6 py-4 md:py-5 ${className}`}
    >
      {/* Flowing water waves */}
      <svg
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-full w-full opacity-70"
        preserveAspectRatio="none"
        viewBox="0 0 800 120"
      >
        <defs>
          <linearGradient id="wave-grad-1" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#29ABE2" stopOpacity="0.00" />
            <stop offset="50%" stopColor="#29ABE2" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#29ABE2" stopOpacity="0.00" />
          </linearGradient>
          <linearGradient id="wave-grad-2" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#1F8A3A" stopOpacity="0.00" />
            <stop offset="50%" stopColor="#1F8A3A" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#1F8A3A" stopOpacity="0.00" />
          </linearGradient>
        </defs>

        {/* Back wave (slower) */}
        <path
          className="eco-wave eco-wave-back"
          fill="url(#wave-grad-1)"
          d="M0,80 C120,40 240,120 360,80 C480,40 600,120 720,80 C840,40 960,120 1080,80 L1080,120 L0,120 Z"
        />
        {/* Mid wave */}
        <path
          className="eco-wave eco-wave-mid"
          fill="url(#wave-grad-2)"
          d="M0,90 C150,60 300,120 450,90 C600,60 750,120 900,90 C1050,60 1200,120 1350,90 L1350,120 L0,120 Z"
        />
        {/* Front crest */}
        <path
          className="eco-wave eco-wave-front"
          fill="rgba(41,171,226,0.35)"
          d="M0,100 C100,85 200,115 300,100 C400,85 500,115 600,100 C700,85 800,115 900,100 L900,120 L0,120 Z"
        />
      </svg>

      {/* Rising bubbles */}
      <span className="pointer-events-none absolute inset-0">
        <span className="eco-bubble" style={{ left: "8%", animationDelay: "0s" }} />
        <span className="eco-bubble" style={{ left: "22%", animationDelay: "0.8s" }} />
        <span className="eco-bubble" style={{ left: "38%", animationDelay: "1.6s" }} />
        <span className="eco-bubble" style={{ left: "55%", animationDelay: "0.4s" }} />
        <span className="eco-bubble" style={{ left: "72%", animationDelay: "1.2s" }} />
        <span className="eco-bubble" style={{ left: "88%", animationDelay: "2s" }} />
      </span>

      {/* Content */}
      <div className="relative z-10 flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#29ABE2] text-white shadow-[0_6px_14px_-4px_rgba(41,171,226,0.7)]">
          <Droplets className="h-4 w-4" />
        </span>
        <p
          data-testid="water-badge-text"
          className="font-display text-base md:text-lg font-semibold leading-snug eco-water-text"
        >
          We use Health Canada–registered disinfectants and
          professional-grade labeled products.
        </p>
      </div>
    </div>
  );
}
